import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DashboardDefinition, DashboardWithCurrent } from '../../api/dashboards.js';

export interface DraftAutosave {
  /** Move the baseline without saving, and drop any save in flight: the
   *  designer replaced its state from the server or from a layout sync,
   *  which is not an edit. */
  resetBaseline(baseline: string): void;
  /** Drop a scheduled save (and the unmount flush) without moving the
   *  baseline — call before Discard so a pending edit cannot race it. */
  cancel(): void;
  /** Re-arm the save after `cancel()` when the action it guarded failed
   *  (e.g. Discard): an edit that still differs from the baseline is
   *  saved again, debounced. */
  resume(): void;
  /** True when `snapshot` is the baseline, i.e. no edit is pending. */
  isBaseline(snapshot: string): boolean;
}

/**
 * CSAGE-496 — debounced draft auto-save shared by both dashboard designers.
 *
 * The baseline is seeded with the snapshot of the FIRST render, i.e. the
 * state the designer loaded. Both designers used to seed it with '' — so
 * the first render always looked like an edit and, one debounce later,
 * wrote the loaded (published) definition over the dashboard's draft.
 * Simply opening a dashboard destroyed a draft saved by MCP or another
 * author.
 *
 * An edit is a snapshot that CHANGED since the hook last looked. A child's
 * mount effect (react-grid-layout compacting the loaded layout) can move
 * the baseline before this hook's own first effect runs; that effect then
 * sees the loaded snapshot again, which is not an edit — counting it as
 * one left "Unsaved" stuck on, and under StrictMode the unmount flush
 * saved it.
 *
 * `onDirty` / `onClean` fire as the state leaves / returns to the
 * baseline; `save` fires `delayMs` after the last change, and once more
 * on unmount if an edit is still waiting (switching dashboards or tabs
 * inside the debounce used to drop it). `save` is read through a ref, so
 * the callback that runs is the latest render's — its closure holds the
 * state the snapshot describes.
 */
export function useDraftAutosave(
  snapshot: string,
  delayMs: number,
  save: () => void,
  onDirty?: () => void,
  onClean?: () => void,
): DraftAutosave {
  const baselineRef = useRef(snapshot);
  const lastSeenRef = useRef(snapshot);
  const pendingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [resumeTick, setResumeTick] = useState(0);
  const saveRef = useRef(save);
  const onDirtyRef = useRef(onDirty);
  const onCleanRef = useRef(onClean);
  saveRef.current = save;
  onDirtyRef.current = onDirty;
  onCleanRef.current = onClean;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => {
    const changed = snapshot !== lastSeenRef.current;
    lastSeenRef.current = snapshot;
    if (snapshot === baselineRef.current) {
      if (pendingRef.current) onCleanRef.current?.();
      pendingRef.current = false;
      return;
    }
    if (!changed && !pendingRef.current) return;
    pendingRef.current = true;
    onDirtyRef.current?.();
    const t = setTimeout(() => {
      timerRef.current = null;
      baselineRef.current = snapshot;
      pendingRef.current = false;
      saveRef.current();
    }, delayMs);
    timerRef.current = t;
    return () => {
      clearTimeout(t);
      if (timerRef.current === t) timerRef.current = null;
    };
    // resumeTick: resume() re-runs this effect for the same snapshot.
  }, [snapshot, delayMs, resumeTick]);

  // Unmount only: flush an edit that is still inside the debounce window.
  useEffect(
    () => () => {
      if (!pendingRef.current) return;
      pendingRef.current = false;
      saveRef.current();
    },
    [],
  );

  const cancel = useCallback(() => {
    clearTimer();
    pendingRef.current = false;
  }, [clearTimer]);
  const resume = useCallback(() => {
    // Forget the last-seen snapshot so the re-run counts it as a change.
    lastSeenRef.current = '\u0000resume';
    setResumeTick((n) => n + 1);
  }, []);
  const resetBaseline = useCallback(
    (baseline: string) => {
      cancel();
      baselineRef.current = baseline;
    },
    [cancel],
  );
  const isBaseline = useCallback((snap: string) => snap === baselineRef.current, []);
  return useMemo(
    () => ({ resetBaseline, cancel, resume, isBaseline }),
    [resetBaseline, cancel, resume, isBaseline],
  );
}

/**
 * CSAGE-496 — what a designer should open: the unpublished draft when one
 * exists (it is the newest work, and what the ?draft=1 preview already
 * renders), else the published version. A draft whose layout kind does not
 * match the dashboard's is ignored rather than fed to the wrong designer.
 */
export function designerSource(
  data: Pick<DashboardWithCurrent, 'layoutKind' | 'currentVersion' | 'draft'>,
): { definition: DashboardDefinition; draftSavedAt: Date | null } | null {
  const draft = data.draft;
  if (draft && draft.definition.layout.kind === data.layoutKind) {
    return { definition: draft.definition, draftSavedAt: new Date(draft.updatedAt) };
  }
  if (!data.currentVersion) return null;
  return { definition: data.currentVersion.definition, draftSavedAt: null };
}
